import { useCallback, useContext } from "react";

// internal-deps
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import {
    clickGiftingApplyEvent,
    clickGiftingApplySelectedEvent,
    clickGiftingCouponDeSelectCheckboxEvent,
    clickGiftingCouponSelectCheckboxEvent,
    clickGiftingCrossPopUpEvent,
    clickGiftingPopUpEvent,
    impressionGiftingApplySelectedEvent,
    impressionGiftingCouponOffersEvent,
    impressionGiftingOffersEvent,
    impressionGiftingPopUpEvent,
    impressionGiftingSearchBarEvent,
} from "@cart-client/analytics/events";

// interfaces
import { CouponCardImpressionPayload } from "@swiggy-private/core-types";
import { GiftingCouponAnalytics } from "../types";
import {
    BaseGiftingPayload,
    GiftingAppliedCouponContextPayload,
    GiftingPopupContextPayload,
    GiftingSavingsContextPayload,
    GiftingOffersContextPayload,
} from "@cart-client/interfaces/Coupon";

export const useGiftingCouponAnalytics = (basePayload: BaseGiftingPayload): GiftingCouponAnalytics => {
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);

    const handleImpressionOffers = useCallback(
        (payload: GiftingOffersContextPayload) => {
            impressionGiftingOffersEvent(impressionEvent, {
                contextPayload: {
                    defaultSelectedOfferIdlist: payload.defaultSelectedOfferIdlist,
                    defaultSelectedCoupon: payload.defaultSelectedCoupon,
                },
                basePayload,
            });
        },
        [basePayload, impressionEvent],
    );

    const handleCouponOffersImpression = useCallback(
        (payload: CouponCardImpressionPayload) => {
            impressionGiftingCouponOffersEvent(impressionEvent, {
                contextPayload: {
                    offerId: payload.offerId || "",
                    coupon: payload.couponCode || "",
                    isApplicable: payload.isApplicable || false,
                },
                basePayload: {
                    ...basePayload,
                    applicabletype: payload.applicabletype,
                },
                additionalParams: {
                    isCouponSelected: payload.isSelected || false,
                    isCouponApplied: payload.isApplied || false,
                    description: payload.description || "",
                },
            });
        },
        [basePayload, impressionEvent],
    );

    const handleSelectCouponCheckboxClick = useCallback(
        (payload: CouponCardImpressionPayload) => {
            clickGiftingCouponSelectCheckboxEvent(clickEvent, {
                contextPayload: {
                    offerId: payload.offerId || "",
                    coupon: payload.couponCode || "",
                    isApplicable: payload.isApplicable || false,
                },
                basePayload: {
                    ...basePayload,
                    applicabletype: payload.applicabletype,
                },
                additionalParams: {
                    isCouponSelected: payload.isSelected || false,
                    isCouponApplied: payload.isApplied || false,
                    description: payload.description || "",
                },
            });
        },
        [basePayload, clickEvent],
    );

    const handleDeselectCouponCheckboxClick = useCallback(
        (payload: CouponCardImpressionPayload) => {
            clickGiftingCouponDeSelectCheckboxEvent(clickEvent, {
                contextPayload: {
                    offerId: payload.offerId || "",
                    coupon: payload.couponCode || "",
                    isApplicable: payload.isApplicable || false,
                },
                basePayload: {
                    ...basePayload,
                    applicabletype: payload.applicabletype,
                },
                additionalParams: {
                    isCouponSelected: payload.isSelected || false,
                    isCouponApplied: payload.isApplied || false,
                    description: payload.description || "",
                },
            });
        },
        [basePayload, clickEvent],
    );

    const handleApplyCouponImpression = useCallback(
        (payload: GiftingSavingsContextPayload) => {
            impressionGiftingApplySelectedEvent(impressionEvent, {
                contextPayload: {
                    savingsCallout: payload.savingsCallout,
                    ctaText: payload.ctaText,
                },
                basePayload,
            });
        },
        [basePayload, impressionEvent],
    );

    const handleApplyCouponClick = useCallback(
        (payload: GiftingAppliedCouponContextPayload) => {
            clickGiftingApplySelectedEvent(clickEvent, {
                contextPayload: {
                    appliedOfferIdList: payload.appliedOfferIdList,
                    appliedCouponList: payload.appliedCouponList,
                    savingsCallout: payload.savingsCallout,
                    ctaText: payload.ctaText,
                },
                basePayload,
            });
        },
        [basePayload, clickEvent],
    );

    const handleSearchBarImpression = useCallback(
        (placeholder: string) => {
            impressionGiftingSearchBarEvent(impressionEvent, { placeholder, basePayload });
        },
        [basePayload, impressionEvent],
    );

    const handleApplyInputBoxClick = useCallback(
        (couponCode: string) => {
            clickGiftingApplyEvent(clickEvent, {
                couponsearchtext: couponCode,
                basePayload,
            });
        },
        [basePayload, clickEvent],
    );

    const handleGiftingModalImpression = useCallback(
        (payload: GiftingPopupContextPayload) => {
            impressionGiftingPopUpEvent(impressionEvent, {
                contextPayload: {
                    title: payload.title,
                    subtitle: payload.subtitle,
                    description: payload.description,
                },
                basePayload,
            });
        },
        [basePayload, impressionEvent],
    );

    const handleGiftingModalCloseClick = useCallback(
        (payload: GiftingPopupContextPayload) => {
            clickGiftingCrossPopUpEvent(clickEvent, {
                contextPayload: {
                    title: payload.title,
                    subtitle: payload.subtitle,
                    description: payload.description,
                },
                basePayload,
            });
        },
        [basePayload, clickEvent],
    );

    const handleGiftingModalCTAClick = useCallback(
        (payload: GiftingPopupContextPayload) => {
            clickGiftingPopUpEvent(clickEvent, {
                contextPayload: {
                    title: payload.title,
                    subtitle: payload.subtitle,
                    description: payload.description,
                },
                basePayload,
            });
        },
        [basePayload, clickEvent],
    );

    return {
        handleImpressionOffers,
        handleCouponOffersImpression,
        handleSelectCouponCheckboxClick,
        handleDeselectCouponCheckboxClick,
        handleApplyCouponImpression,
        handleApplyCouponClick,
        handleSearchBarImpression,
        handleApplyInputBoxClick,
        handleGiftingModalImpression,
        handleGiftingModalCloseClick,
        handleGiftingModalCTAClick,
    };
};
